import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {
	Divider,
	IconButton,
	Layout,
	Text,
	Tooltip,
} from '@phonectas/unifon-ui-kit';
import {formatInfo, getSatisfactionDetails} from 'utils/div';
import {useCopyToClipboard} from 'common/hooks/useCopyToClipboard';
import TextWithLabel from 'common/components/TextWithLabel';
import UnifonStarIcon from 'common/components/icons/UnifonStarIcon';
import {StyledTrustpilotButton} from 'common/components/styled';
import NewCopyIcon from 'common/components/icons/NewCopyIcon';
import NewWindowIcon from 'common/components/icons/NewWindowIcon';
import {SALES_CRM_URL} from 'common/constants';
import {Account} from 'common/types/store/threeSixtyTypes';

interface SmallViewDetailsProps {
	data: Account;
	showTrustpilot: () => void;
	showBytt: () => void;
}

const SmallViewDetails: React.FC<SmallViewDetailsProps> = ({data, showTrustpilot, showBytt}) => {
	const [t] = useTranslation();
	const [copyToClipboard] = useCopyToClipboard();
	const handleCopy = useCallback((e: React.SyntheticEvent, value: string) => {
		e.stopPropagation();
		copyToClipboard(value);
	}, [copyToClipboard]);
	const accountDetails = data?.account_details;
	const isOrgNumber = accountDetails?.vat_number;
	const mobiles = accountDetails?.products.mobiles_active_count;
	const satisfaction = accountDetails?.satisfaction_rating;
	const satisfactionRating = getSatisfactionDetails(satisfaction);
	const isNumberOfEmployees = accountDetails?.employers_count;
	const isCustomerResponsible = accountDetails?.customer_responsible;
	const handleOpenSalesCrm = useCallback(() => {
		window.open(`${SALES_CRM_URL}${data?.scrm_order_id}`, '_blank');
	}, [data?.scrm_order_id]);

	return (
		<>
			<Layout fullWidth direction="column" gap="sm">
				<Divider />
				<Layout
					fullWidth
					direction="row"
					style={{padding: '16px 16px 5px 16px'}}
					justifyContent="space-between"
					gap="sm"
				>
					<Layout>
						{
							isCustomerResponsible && (
								<TextWithLabel
									label={t('Customer responsible')}
									text={accountDetails?.customer_responsible}
								/>
							)
						}
						{
							isOrgNumber && (
								<TextWithLabel label={t('Org. number')} text={formatInfo(accountDetails?.vat_number)} />
							)
						}
						{
							mobiles && (
								<TextWithLabel label={t('Mobiles')} text={mobiles} />
							)
						}
						{
							isNumberOfEmployees && (
								<TextWithLabel label={t('Number of employees')} text={isNumberOfEmployees} />
							)
						}
						{
							satisfaction && (
								<TextWithLabel
									label={t('Satisfaction')}
									textColor={satisfactionRating.colorCode}
									text={satisfactionRating.text}
									toUpperCase
								/>
							)
						}
					</Layout>
					<Layout>
						{
							accountDetails?.last_viewed_by && (
								<TextWithLabel label={t('Last viewed by')} text={accountDetails?.last_viewed_by} />
							)
						}
					</Layout>
				</Layout>
				<Layout direction="row" alignItems="center" style={{padding: '16px'}}>
					{
						data?.cdr_account_number && (
							<Layout alignItems="center" gap="none">
								<Text variant="body1" style={{fontSize: '18px'}}>{data?.cdr_account_number}</Text>
								<IconButton onClick={(e) => handleCopy(e, String(data?.cdr_account_number))} size="xs">
									<NewCopyIcon />
								</IconButton>
							</Layout>
						)
					}
					{
						data?.scrm_order_id && (
							<Layout alignItems="center" gap="none">
								<Text variant="body1" style={{fontSize: '18px'}}>{t('Sales CRM')}</Text>
								<IconButton id="salesCrm" size="xs" onClick={handleOpenSalesCrm}>
									<NewWindowIcon color="#9798A9" />
								</IconButton>
							</Layout>
						)
					}
					<Tooltip text={t('Send review request (Trustpilot)')}>
						<StyledTrustpilotButton
							onClick={showTrustpilot}
							icon={<UnifonStarIcon />}
							color="primary"
							variant="action"
						>
							{t('Trustpilot')}
						</StyledTrustpilotButton>
					</Tooltip>
					<StyledTrustpilotButton
						onClick={showBytt}
						color="primary"
						variant="action"
					>
						{t('Bytt.no')}
					</StyledTrustpilotButton>
				</Layout>
			</Layout>
		</>
	);
}

export default SmallViewDetails;
